import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import moment, { Moment } from "moment-timezone";

import {
    ControlResultStepWithoutFields,
    ExistingControlResultPaginatedListDto as ControlResultPaginatedListDto,
    ExistingControlResult,
    FutureControlResult,
} from "api/generated/model";
import { ActivityNumbers } from "components/activityPieChart";

export type ExistingControlActivityStatus =
    | "Completed"
    | "CompletedAfterDueDate"
    | "Pending"
    | "Overdue";

export type ControlActivityStatus = ExistingControlActivityStatus | "Future";

/**
 * Extract existing control results from the paginated list of control results.
 */
export function extractExistingControlResults(
    dtoList: ControlResultPaginatedListDto
): Array<ExistingControlResult> {
    return dtoList.content.map((crDto) => crDto.resource);
}

/**
 * Compute the status of an existing control activity based on its final step.
 */
export function computeExistingControlActivityStatus(
    existingControlResult: ExistingControlResult,
    now: Moment
): ExistingControlActivityStatus {
    const event = computeEvent(existingControlResult, now);
    return convertEventTypeToExistingControlActivityStatus(event.eventType);
}

/**
 * Get the final step of an existing control result.
 */
export function getFinalStepForExistingControlResult(
    existingControlResult: ExistingControlResult
): ControlResultStepWithoutFields {
    const sg = existingControlResult.stepGroup;
    if (sg.subsequentSteps.length === 0) {
        return sg.initialStep;
    } else {
        return sg.subsequentSteps[sg.subsequentSteps.length - 1];
    }
}

/**
 * Compute percentage, defensively handling edge cases.
 * Returns a value between 0 and 100.
 * The returned value is rounded to one decimal place.
 *
 * In case if total is 0 returns null.
 * In case if part is greater than total throws an error.
 *
 * ## Example
 *
 * ```ts
 * computePercentage(50, 200); // returns 25.0
 * ```
 */
function computePercentage(count: number, totalCount: number): number | null {
    if (totalCount === 0) {
        return null;
    }
    if (count > totalCount) {
        throw new Error(
            `Part (${count}) cannot be greater than total (${totalCount})`
        );
    }

    // Round to one decimal place. E.g. `37.1`
    return Math.round((count / totalCount) * 1000) / 10;
}

export function computeOnTimeRatePercentage(
    statusCount: ActivityNumbers
): number | null {
    // NOTE: pending are explicitly not counted in the on-time rate
    const totalCount =
        statusCount.completed +
        statusCount.completedAfterDue +
        statusCount.overdue;

    return computePercentage(statusCount.completed, totalCount);
}

export function computeCompletionRatePercentage(
    statusCount: ActivityNumbers
): number | null {
    // All activities including the future ones are counted in the completion rate
    const total =
        statusCount.completed +
        statusCount.completedAfterDue +
        statusCount.pending +
        statusCount.overdue +
        statusCount.toBeSent;

    const totalCompleted =
        statusCount.completed + statusCount.completedAfterDue;
    return computePercentage(totalCompleted, total);
}

export function computeActivitiesCountedByStatus(
    controlResults: Array<ExistingControlResult>,
    futureControlResults: FutureControlResult[],
    now: Moment
): ActivityNumbers {
    const countByStatus = (status: ControlActivityStatus): number => {
        if (status === "Future") {
            return futureControlResults.length;
        }
        return controlResults.filter(
            (cr) => computeExistingControlActivityStatus(cr, now) === status
        ).length;
    };

    return {
        completed: countByStatus("Completed"),
        completedAfterDue: countByStatus("CompletedAfterDueDate"),
        pending: countByStatus("Pending"),
        overdue: countByStatus("Overdue"),
        toBeSent: countByStatus("Future"),
    };
}

/**
 * Essentially an extended version of  ExistingControlActivityStatus.
 * It's used in DashboardControlActivities (aka Latest Events table) but
 * also suits as as medium to compute ExistingControlActivityStatus, that allows
 * as to have a single computational logic to maintain.
 */
export type EventType =
    | "Completed"
    | "CompletedAfterDueDate"
    | "PendingOnResponsible"
    | "PendingOnFirstReviewer"
    | "PendingOnSecondReviewer"
    | "OverdueOnResponsible"
    | "OverdueOnFirstReviewer"
    | "OverdueOnSecondReviewer";

/**
 * Represents a status of a control activity in DashboardControlActivities.
 * NOTE: the semantic meaning of `datetime` is different for different event types.
 */
export interface Event {
    eventType: EventType;
    datetime: Moment;
}

export function computeEvent(
    existingControlResult: ExistingControlResult,
    now: Moment
): Event {
    const finalStep = getFinalStepForExistingControlResult(
        existingControlResult
    );

    // If the CR is completed (in time or after the due date)
    if (finalStep.performance?.performedAt !== undefined) {
        // If the CR is completed
        const completedOn = moment(finalStep.performance.performedAt);
        const dueDate = moment(finalStep.dueDate);
        const eventType =
            completedOn <= dueDate ? "Completed" : "CompletedAfterDueDate";
        return { eventType, datetime: completedOn };
    }

    const pendingRoleStep = getPendingRoleStep(existingControlResult);
    if (!pendingRoleStep) {
        // This should not happen, because we already checked that the CR is not completed.
        throw new Error(
            "Control result is pending, but no pending step is found"
        );
    }

    const { role, previousStep, step: pendingStep } = pendingRoleStep;
    const isOverdue = moment(pendingStep.dueDate).isBefore(now);

    switch (role) {
        case "Responsible": {
            // For responsible as time stamp we use the created date of the CR (aka sent out date).
            const datetime = moment(existingControlResult.created);
            const eventType = isOverdue
                ? "OverdueOnResponsible"
                : "PendingOnResponsible";
            return { eventType, datetime };
        }
        case "Reviewer": {
            const prevStepPerformedAt = previousStep?.performance?.performedAt;
            if (prevStepPerformedAt === undefined) {
                throw new Error(
                    "Pending step is a reviewer step, but no previous step was not performed. Something went wrong."
                );
            }
            const datetime = moment(prevStepPerformedAt);
            const eventType = computeReviewerEventType(
                existingControlResult,
                pendingStep,
                isOverdue
            );
            return { eventType, datetime };
        }
        default: {
            return assertNever(role);
        }
    }
}

function computeReviewerEventType(
    existingControlResult: ExistingControlResult,
    pendingStep: ControlResultStepWithoutFields,
    isOverdue: boolean
): EventType {
    const reviewerSteps = existingControlResult.stepGroup.subsequentSteps;
    const isFirstReviewer = pendingStep === reviewerSteps[0];
    if (isFirstReviewer) {
        return isOverdue ? "OverdueOnFirstReviewer" : "PendingOnFirstReviewer";
    }
    return isOverdue ? "OverdueOnSecondReviewer" : "PendingOnSecondReviewer";
}

export function localizeEvent(eventType: EventType, t: TFunction): string {
    switch (eventType) {
        case "Completed":
            return t("DashboardControlActivities_Event_Completed");
        case "CompletedAfterDueDate":
            return t("DashboardControlActivities_Event_CompletedAfterDueDate");
        case "PendingOnResponsible":
            return t("DashboardControlActivities_Event_PendingOnResponsible");
        case "PendingOnFirstReviewer":
            return t("DashboardControlActivities_Event_PendingOnFirstReviewer");
        case "PendingOnSecondReviewer":
            return t(
                "DashboardControlActivities_Event_PendingOnSecondReviewer"
            );
        case "OverdueOnResponsible":
            return t("DashboardControlActivities_Event_OverdueOnResponsible");
        case "OverdueOnFirstReviewer":
            return t("DashboardControlActivities_Event_OverdueOnFirstReviewer");
        case "OverdueOnSecondReviewer":
            return t(
                "DashboardControlActivities_Event_OverdueOnSecondReviewer"
            );
        default:
            return assertNever(eventType);
    }
}

export type ActorRole = "Responsible" | "Reviewer";

export interface RoleStep {
    role: ActorRole;
    step: ControlResultStepWithoutFields;
    previousStep: ControlResultStepWithoutFields | null;
}

function getInitialRoleStep(
    existingControlResult: ExistingControlResult
): RoleStep {
    return {
        step: existingControlResult.stepGroup.initialStep,
        role: "Responsible",
        previousStep: null,
    };
}

export function computeRoleStep(
    existingControlResult: ExistingControlResult
): RoleStep {
    const pendingStep = getPendingRoleStep(existingControlResult);
    // No pending step means the activity is completed (Completed or CompletedAfterDueDate),
    // so in that case we always want to shown name of Responsible.
    const roleStep =
        pendingStep !== null
            ? pendingStep
            : getInitialRoleStep(existingControlResult);

    return roleStep;
}
/**
 * Find first unperformed step in the existing control result.
 * If all steps are performed return null.
 */
function getPendingRoleStep(
    existingControlResult: ExistingControlResult
): RoleStep | null {
    const sg = existingControlResult.stepGroup;

    if (!sg.initialStep.performance) {
        return {
            step: sg.initialStep,
            role: "Responsible",
            previousStep: null,
        };
    }
    let previousStep = sg.initialStep;
    for (const step of sg.subsequentSteps) {
        if (!step.performance) {
            return {
                step,
                role: "Reviewer",
                previousStep,
            };
        }
        previousStep = step;
    }
    return null;
}

export function convertEventTypeToExistingControlActivityStatus(
    eventType: EventType
): ExistingControlActivityStatus {
    switch (eventType) {
        case "Completed":
            return "Completed";
        case "CompletedAfterDueDate":
            return "CompletedAfterDueDate";
        case "PendingOnResponsible":
        case "PendingOnFirstReviewer":
        case "PendingOnSecondReviewer":
            return "Pending";
        case "OverdueOnResponsible":
        case "OverdueOnFirstReviewer":
        case "OverdueOnSecondReviewer":
            return "Overdue";
        default:
            return assertNever(eventType);
    }
}
